import { Link, useParams } from 'react-router-dom'
import {
  FASCIST_TRACK_SIZE,
  LIBERAL_TRACK_SIZE,
  MAX_PLAYERS,
  MIN_PLAYERS,
  ROLE_DISTRIBUTION,
  fascistTrack,
  hitlerKnowsFascists,
} from '@sh/engine'
import type { PowerKind } from '@sh/shared'
import { POWER_LABEL, PolicyTile } from '../../components/Board.js'
import { PowerIcon } from '../../components/Icons.js'
import { NotFound } from '../NotFound.js'
import { Article } from './Article.js'

/**
 * One page per table size, because the game genuinely differs at each one.
 *
 * "How does Secret Hitler play with seven?" is a question people actually ask,
 * and the answers on the web are forum threads. The answer here is read
 * straight out of the engine's own tables - the role split, whether Hitler is
 * told who his fascists are, and which powers this board hands out - so it
 * cannot be wrong about the game it is served alongside.
 *
 * One component rather than six files: the page is a function of `n` and
 * nothing else, so six copies would be five chances to mistype a number.
 */

const COUNTS = Object.keys(ROLE_DISTRIBUTION)
  .map(Number)
  .sort((a, b) => a - b)

/** The sentence that makes a table size worth its own page. */
function whatChanges(n: number): string {
  if (n <= 6) {
    return (
      'A small table, and the most claustrophobic version of the game. Hitler is told who his ' +
      'fascists are, so the fascist side always plays with full information - but there are so ' +
      'few of them that a single execution can end it.'
    )
  }
  if (n <= 8) {
    return (
      'The size where Hitler goes blind. From seven players up he is not told who his fascists ' +
      'are, so he has to work out who is protecting him from the way they vote - and a fascist ' +
      'has to protect him without ever being able to say so.'
    )
  }
  return (
    'The largest game, and the loudest. Three fascists plus a Hitler who does not know them, ' +
    'against six liberals who cannot hear each other think. Investigations matter most here, ' +
    'because there are simply too many faces to read.'
  )
}

export function PlayerCount() {
  const { count } = useParams()
  const n = Number(count)
  const dist = ROLE_DISTRIBUTION[n]
  // A page for a table size the game does not have is not a page. Rendering
  // the 404 rather than redirecting keeps `/play-with/99` honest for a crawler.
  if (!dist || !Number.isInteger(n)) return <NotFound />

  const track = fascistTrack(n)
  const powers = track.filter((p): p is PowerKind => p !== null)
  const unique = [...new Set(powers)]
  const knows = hitlerKnowsFascists(n)

  return (
    <Article
      title={`Secret Hitler with ${n} players`}
      standfirst={
        <>
          Who is dealt what, whether <strong>Hitler</strong> knows his team, and which powers this
          board hands out. Every number here is read from the same tables the game itself runs on.
        </>
      }
    >
      <section className="card">
        <h2 className="card__title">The table</h2>
        <ul className="rules__parties">
          <li className="rules__party">
            <span className="rules__tag rules__tag--liberal">{dist.liberals} liberals</span>
            <span>No idea who anyone is. They are the majority and that is all they have.</span>
          </li>
          <li className="rules__party">
            <span className="rules__tag rules__tag--fascist">
              {dist.fascists} {dist.fascists === 1 ? 'fascist' : 'fascists'}
            </span>
            <span>
              {dist.fascists === 1
                ? 'Knows exactly who Hitler is, and is the only one who does.'
                : 'They know each other, and they know who Hitler is.'}
            </span>
          </li>
          <li className="rules__party">
            <span className="rules__tag rules__tag--fascist">1 Hitler</span>
            <span>
              {knows
                ? 'Told who his fascists are at this table size.'
                : 'Not told who his fascists are. He is as blind as a liberal.'}
            </span>
          </li>
        </ul>
        <p className="card__hint">{whatChanges(n)}</p>
      </section>

      <section className="card">
        <h2 className="card__title">This board</h2>
        <p className="card__hint">
          The powers sit on the fascist track, and which slot grants which depends on how many
          people sat down. This is the board a {n}-player game plays on:
        </p>
        <div className="rules__track" role="list">
          {track.map((power, i) => (
            <div className="rules__slot" role="listitem" key={i}>
              <PolicyTile
                policy="fascist"
                filled={false}
                power={power}
                victory={i === FASCIST_TRACK_SIZE - 1}
              />
            </div>
          ))}
        </div>
        <ul className="rules__marks">
          {unique.map((kind) => (
            <li className="rules__mark" key={kind}>
              <span className="rules__mark-icon" aria-hidden="true">
                <PowerIcon kind={kind} />
              </span>
              {POWER_LABEL[kind]}
            </li>
          ))}
        </ul>
        <p className="card__hint">
          {unique.includes('investigate')
            ? 'Investigate Loyalty is on this board, so a President can learn one player’s party.'
            : 'Investigate Loyalty never appears at this table size — nobody gets to check a card.'}{' '}
          <Link to="/rules#powers">What each power does</Link>.
        </p>
      </section>

      <section className="card">
        <h2 className="card__title">Winning</h2>
        <p className="card__hint">
          Unchanged at every table size: {LIBERAL_TRACK_SIZE} liberal policies or a dead Hitler wins
          it for the liberals, {FASCIST_TRACK_SIZE} fascist policies or Hitler elected Chancellor
          late wins it for the fascists. Only the odds move, and they move with this table.
        </p>
        <p className="card__hint">
          <Link to="/rules">The full rules</Link>, or{' '}
          <Link to="/">start a game for {n}</Link>.
        </p>
      </section>

      <section className="card">
        <h2 className="card__title">Other table sizes</h2>
        <p className="card__hint">
          The game runs from {MIN_PLAYERS} to {MAX_PLAYERS}.
        </p>
        <ul className="rules__marks">
          {COUNTS.filter((other) => other !== n).map((other) => (
            <li className="rules__mark" key={other}>
              <Link to={`/play-with/${other}`}>{other} players</Link>
            </li>
          ))}
        </ul>
      </section>
    </Article>
  )
}
